import React from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Checkbox,
  Typography,
  Autocomplete,
  Alert,
} from '@mui/material';
import {
  DispositivoRedeTipo,
  DISPOSITIVO_REDE_TIPOS,
} from '../../../services/dispositivoRedeService';
import { UseDispositivosRedeReturn } from './useDispositivosRede';

type DispositivoFormDialogProps = Pick<
  UseDispositivosRedeReturn,
  | 'formOpen'
  | 'setFormOpen'
  | 'formData'
  | 'setFormData'
  | 'editing'
  | 'saving'
  | 'handleSave'
  | 'servicoOptions'
  | 'servicoSearchLoading'
  | 'selectedServico'
  | 'setSelectedServico'
  | 'handleServicoSearch'
  | 'getServicoLabel'
>;

const DispositivoFormDialog: React.FC<DispositivoFormDialogProps> = ({
  formOpen,
  setFormOpen,
  formData,
  setFormData,
  editing,
  saving,
  handleSave,
  servicoOptions,
  servicoSearchLoading,
  selectedServico,
  setSelectedServico,
  handleServicoSearch,
  getServicoLabel,
}) => (
  <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Editar dispositivo' : 'Novo dispositivo'}</DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label="Nome"
          value={formData.nome}
          onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))}
          fullWidth
          required
        />

        <FormControl fullWidth required>
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={formData.tipo}
            onChange={(e) => {
              const tipo = e.target.value as DispositivoRedeTipo;
              setFormData((prev) => ({
                ...prev,
                tipo,
                contrato_servico_id: tipo === 'cliente' ? prev.contrato_servico_id : null,
              }));
              if (tipo !== 'cliente') setSelectedServico(null);
            }}
          >
            {DISPOSITIVO_REDE_TIPOS.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {formData.tipo === 'cliente' ? (
          <>
            <Autocomplete
              options={servicoOptions}
              loading={servicoSearchLoading}
              value={selectedServico}
              onChange={(_, value) => {
                setSelectedServico(value);
                setFormData((prev) => ({
                  ...prev,
                  contrato_servico_id: value?.id ?? null,
                  nome: prev.nome || (value ? getServicoLabel(value) : ''),
                }));
              }}
              onInputChange={(_, value) => void handleServicoSearch(value)}
              getOptionLabel={(option) => getServicoLabel(option)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Serviço do contrato (internet)"
                  placeholder="Busque por cliente, PPPoE ou IP..."
                  required
                />
              )}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Modo de Monitoramento:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(formData.modo_monitoramento || 'ping_ip') === 'ping_ip'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({ ...prev, modo_monitoramento: 'ping_ip' }));
                        }
                      }}
                    />
                  }
                  label="Ping por IP"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.modo_monitoramento === 'status_conexao'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({ ...prev, modo_monitoramento: 'status_conexao' }));
                        }
                      }}
                    />
                  }
                  label="Status da Conexão"
                />
              </Box>
            </Box>

            {selectedServico && (formData.modo_monitoramento || 'ping_ip') === 'ping_ip' && (
              <Alert severity="info" sx={{ py: 0, borderRadius: 2 }}>
                IP do serviço: {selectedServico.endereco_ip || 'não cadastrado — será usado o IP da conexão RADIUS online'}
              </Alert>
            )}

            {selectedServico && formData.modo_monitoramento === 'status_conexao' && (
              <Alert severity="info" sx={{ py: 0, borderRadius: 2 }}>
                O monitoramento irá checar se o status da conexão RADIUS do usuário ({selectedServico.usuario_pppoe || 'não informado'}) está online ou offline.
              </Alert>
            )}
          </>
        ) : (
          <TextField
            label="Endereço IP ou hostname"
            value={formData.endereco_ip}
            onChange={(e) => setFormData((prev) => ({ ...prev, endereco_ip: e.target.value }))}
            fullWidth
            required
            placeholder={
              formData.tipo === 'servicos'
                ? 'Ex: 8.8.8.8, google.com ou https://www.exemplo.com'
                : 'Ex: 192.168.1.1 ou switch.local'
            }
            helperText="Aceita IP, domínio ou URL (protocolo e caminho são ignorados no ping)."
          />
        )}

        <TextField
          label="Monitorar até (Opcional)"
          type="date"
          value={formData.monitorar_ate || ''}
          onChange={(e) => setFormData((prev) => ({ ...prev, monitorar_ate: e.target.value }))}
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText="Se preenchido, o dispositivo será automaticamente removido do banco após essa data."
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.ativo !== false}
              onChange={(e) => setFormData((prev) => ({ ...prev, ativo: e.target.checked }))}
            />
          }
          label="Ativo para monitoramento"
        />
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2 }}>
        {saving ? 'Salvando...' : 'Salvar'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default DispositivoFormDialog;
